import { Editor, useEditorState } from '@tiptap/react';
import { FunctionComponent, useCallback, useMemo } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { BlockTypes } from '../../../../models';
import { Select } from '../../../Select';

import classNames from 'classnames';
import classes from './Media.module.scss';

interface MediaProps {
  editor: Editor;
}

export const Media: FunctionComponent<MediaProps> = ({ editor }) => {
  const { t } = useTranslation();

  const {
    isActiveImage,
    isActiveAudio,
    isActiveVideo,
    isActivePdf,
    isActiveFile,
    isActiveParagraph,
    isActiveList,
    isActiveTable,
  } = useEditorState({
    editor,
    selector: ({ editor }) => ({
      isActiveImage: editor.isActive('imageNode'),
      isActiveAudio: editor.isActive('audioNode'),
      isActiveVideo: editor.isActive('videoNode'),
      isActivePdf: editor.isActive('pdfNode'),
      isActiveFile: editor.isActive('fileNode'),
      isActiveParagraph: editor.isActive('paragraph'),
      isActiveList:
        editor.isActive('taskList') ||
        editor.isActive('orderedList') ||
        editor.isActive('bulletList'),
      isActiveTable: editor.isActive('table'),
    }),
  });

  const items = useMemo(
    () => [
      { id: BlockTypes.image, title: t('richTextEditor.tool-names-image-gallery') },
      { id: BlockTypes.audio, title: t('richTextEditor.tool-names-audio') },
      { id: BlockTypes.video, title: t('richTextEditor.tool-names-video') },
      { id: BlockTypes.pdf, title: t('richTextEditor.tool-names-pdf') },
      { id: BlockTypes.file, title: t('richTextEditor.tool-names-file') },
    ],
    [t]
  );

  const onChange = useCallback(
    (type: BlockTypes) => {
      switch (type) {
        case BlockTypes.image:
          return editor.commands.insertImageNode({
            items: [],
            withBorder: false,
            withBackground: false,
            stretched: false,
          });
        case BlockTypes.audio:
          return editor.commands.insertAudioNode({ items: [] });
        case BlockTypes.video:
          return editor.commands.insertVideoNode({ items: [] });
        case BlockTypes.pdf:
          return editor.commands.insertPdfNode({ items: [] });
        case BlockTypes.file:
          return editor.commands.insertFileNode({ items: [] });
      }
    },
    [editor]
  );

  const selectedItemId = isActiveImage
    ? BlockTypes.image
    : isActiveAudio
    ? BlockTypes.audio
    : isActiveVideo
    ? BlockTypes.video
    : isActivePdf
    ? BlockTypes.pdf
    : isActiveFile
    ? BlockTypes.file
    : undefined;

  return (
    <Select
      className={classNames(classes['media'], { [classes['media--mobile']]: isMobileOnly })}
      placeholderLabel={t('common.media')}
      selectedItemId={selectedItemId}
      active={Boolean(selectedItemId)}
      items={items}
      onChange={(type) => onChange(type as BlockTypes)}
      disabled={!isActiveParagraph || isActiveList || isActiveTable}
      {...(isMobileOnly && { compactSidePadding: true, listMinWidthRem: 10.5 })}
      noBorder
    />
  );
};
